import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { find, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Grid } from '@material-ui/core';
import Validation from '../../Validation/Validation';
import {
  getCityVillages,
  selectSortedCityVillages,
  selectSortedCountries,
  selectSortedRAStates,
} from '../../../redux/modules/countries';
import { translitArmStreet } from '../../../helpers/translit';
import { YEREVAN_CITY_VILLAGE } from '../../../constants';
import TextInputFieldWithValidation from '../../ValidationFormElements/TextInputFieldWithValidation';
import MainSelect from '../../MUI/MainSelect';
import ArmenianRegionSelectOptions from './ArmenianRegionSelectOptions';
import ForeignLanguageAddressFields from './ForeignLanguageAddressFields';
import ArmenianLanguageAddressFields from './ArmenianLanguageAddressFields';
import useRegisterValidationFields from '../../../hooks/useRegisterValidationFields';
import { findIsYerevan, isArmenianCountry } from '../../../helpers/countryHelper';
import { getMenuLabelWithTranslation } from '../../../helpers/selectHelpers';
import { isArmenianLanguage } from '../../../helpers/langHelpers';

const ContactInfo = (props) => {
  const {
    formProps,
    initialData,
    showEngAddress,
    disabledInputs,
    hasPhone,
    hasLanguageSensitiveNames,
    hasAddressInputsLanguageSensitiveNames,
  } = props;
  const { i18n } = useTranslation();
  const { register, setValue, errors, clearError, triggerValidation, getValues, watch } = formProps;
  const dispatch = useDispatch();
  const selectedState = useRef();

  const { cityVillagesLoading } = useSelector((store) => store.countriesReducer);
  const countries = useSelector((state) => selectSortedCountries(state, i18n.language));
  const states = useSelector((state) => selectSortedRAStates(state, i18n.language));
  const cityVillages = useSelector((state) => selectSortedCityVillages(state, i18n.language));
  const isYerevan = useMemo(() => findIsYerevan(getValues().state), [getValues]);

  const [isArmenianAddress, setIsArmenianAddress] = useState(false);

  watch(['country', 'state', 'cityVillage', 'street', 'streetEn', 'cellPhone', 'phone']);
  useRegisterValidationFields(
    ['country', 'state', 'cityVillage', 'street', 'streetEn', 'cellPhone', 'phone'],
    register,
  );

  useEffect(() => {
    setValue('country', initialData.country);
    setValue('state', initialData.state);
    setValue('cityVillage', initialData.cityVillage);
  }, [initialData, setValue]);

  const handleChangeInput = useCallback(
    (key, value, shouldValidate = true) => setValue(key, value, shouldValidate),
    [setValue],
  );

  const handleStreetChange = useCallback(
    (value) => {
      const modifiedValue = value && value.replace('․', '.');
      handleChangeInput('street', modifiedValue, !!value);
    },
    [handleChangeInput],
  );

  const handleStreetEnChange = useCallback(
    (value) => {
      const modifiedValue = value && value.replace('․', '.');
      handleChangeInput('streetEn', modifiedValue, !!value);
    },
    [handleChangeInput],
  );

  const handleStreetChangeWithTranslation = useCallback((value) => handleStreetChange(value), [
    handleStreetChange,
  ]);

  const onCityVillageChange = useCallback(
    (value) => {
      handleChangeInput('cityVillage', value);
    },
    [handleChangeInput],
  );

  const onStateChange = useCallback(
    (value) => {
      handleChangeInput('state', value);
      if (findIsYerevan(value)) {
        handleChangeInput('cityVillage', YEREVAN_CITY_VILLAGE, false);
      } else {
        handleChangeInput('cityVillage', null, false);
      }

      if (selectedState.current !== value.id) {
        dispatch(getCityVillages(value.id));
      }
      selectedState.current = value.id;
    },
    [handleChangeInput, dispatch],
  );

  const onCountryChange = useCallback(
    (value) => {
      setIsArmenianAddress(isArmenianCountry(value));
      handleChangeInput('country', value);
      if (states.length && isArmenianCountry(value)) {
        onStateChange(states[0]);
      } else {
        handleChangeInput('state', null, false);
        handleChangeInput('cityVillage', null, false);
      }
    },
    [handleChangeInput, onStateChange, states],
  );

  useEffect(() => {
    if (initialData.country) {
      const foundCountry = find(countries, (c) => c.id === initialData.country.id);
      if (foundCountry) {
        setIsArmenianAddress(foundCountry.label === 'ARM');
        handleChangeInput('country', foundCountry);
      }
    } else {
      handleChangeInput('country', null, false);
      setIsArmenianAddress(false);
    }
  }, [handleChangeInput, countries, initialData, setValue]);

  useEffect(() => {
    if (initialData.state) {
      const foundState = find(states, (s) => s.id === initialData.state.id);
      if (foundState) {
        handleChangeInput('state', foundState, true);
        if (foundState.id !== selectedState.current) {
          dispatch(getCityVillages(foundState.id));
        }
      }
    } else {
      handleChangeInput('state', null, true);
    }
  }, [handleChangeInput, dispatch, initialData, setValue, states]);

  useEffect(() => {
    if (initialData.cityVillage) {
      const foundCityVillage = find(
        cityVillages, (c) => c.id === initialData.cityVillage.id,
      ) || null;
      handleChangeInput('cityVillage', foundCityVillage, !!foundCityVillage);
    } else if (cityVillages.length === 1) {
      onCityVillageChange(cityVillages[0]);
    } else {
      handleChangeInput('cityVillage', null, false);
    }
  }, [initialData, cityVillages, onCityVillageChange, handleChangeInput]);

  useEffect(() => {
    if (!initialData.streetEn) {
      handleStreetChangeWithTranslation(initialData.street);
    } else {
      handleStreetChange(initialData.street);
      handleStreetEnChange(initialData.streetEn);
    }
    setValue('cellPhone', initialData.cellPhone || '', Boolean(initialData.cellPhone));
    setValue('phone', initialData.phone || '', Boolean(initialData.phone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, setValue]);

  useEffect(() => {
    if (getValues().cellPhone || getValues().phone) {
      triggerValidation({ name: 'cellPhone' });
      triggerValidation({ name: 'phone' });
    }
    if (getValues().street) {
      triggerValidation({ name: 'street' });
    }
    if (getValues().streetEn) {
      triggerValidation({ name: 'streetEn' });
    }
  }, [getValues, triggerValidation]);

  useEffect(() => {
    if (countries.length && !initialData.country) {
      onCountryChange(countries[0]);
    }
  }, [countries, onCountryChange, initialData.country]);

  useEffect(() => {
    if (states.length && !initialData.state) {
      onStateChange(states[0]);
    }
  }, [initialData.state, onStateChange, states]);

  const getLabelWithTranslation = useCallback(
    (option) => getMenuLabelWithTranslation(option, i18n.language),
    [i18n],
  );

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
            md={!isEmpty(getValues().state)
          && isArmenianAddress && !isYerevan ? 4 : 6}
          >
            <Validation name="country" errors={errors} clearError={clearError}>
              <FormControl fullWidth>
                <MainSelect
                  disabled={disabledInputs.includes('country')}
                  name="country"
                  label="contactInfo.country"
                  value={getValues().country}
                  options={countries}
                  getMenuLabel={getLabelWithTranslation}
                  onChange={onCountryChange}
                  required
                />
              </FormControl>
            </Validation>
          </Grid>
          {isArmenianAddress && (
            <ArmenianRegionSelectOptions
              isYerevan={isYerevan}
              cityVillages={cityVillages}
              onCityVillageChange={onCityVillageChange}
              cityVillagesLoading={cityVillagesLoading}
              errors={errors}
              clearError={clearError}
              states={states}
              form={{
                cityVillage: {
                  value: getValues().cityVillage,
                  label: 'contactInfo.cityVillage',
                  name: 'cityVillage',
                },
                state: {
                  value: getValues().state,
                  label: 'contactInfo.state',
                  name: 'state',
                },
              }}
              onStateChange={onStateChange}
              disabledInputs={disabledInputs}
            />
          )}
        </Grid>
      </Grid>
      {hasLanguageSensitiveNames && !isArmenianLanguage(i18n.language) ? (
        <ForeignLanguageAddressFields
          disabledInputs={disabledInputs}
          form={getValues()}
          formProps={formProps}
          showEngAddress={showEngAddress}
          handleStreetEnChange={(value) => {
            handleStreetEnChange(value);
            handleChangeInput('street', translitArmStreet(value), !!value);
          }}
          handleStreetChange={handleStreetChange}
        />
      ) : (
        <ArmenianLanguageAddressFields
          disabledInputs={disabledInputs}
          form={getValues()}
          formProps={formProps}
          enStreetRequired={!isArmenianLanguage(i18n.language)}
          handleStreetChangeWithTranslation={handleStreetChangeWithTranslation}
          handleStreetChange={handleStreetChange}
          hasLanguageSensitiveNames={hasLanguageSensitiveNames}
          hasAddressInputsLanguageSensitiveNames={hasAddressInputsLanguageSensitiveNames}
          showEngAddress={showEngAddress}
          handleStreetEnChange={(value) => {
            if (hasAddressInputsLanguageSensitiveNames) {
              handleStreetEnChange(value);
            } else {
              handleStreetEnChange(value);
              handleChangeInput('street', translitArmStreet(value), !!value);
            }
          }}
        />
      )}
      {hasPhone && (
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <TextInputFieldWithValidation
                required={!getValues().phone}
                formProps={formProps}
                labelKey="contactInfo.mobileNumber"
                name="cellPhone"
                autoComplete="off"
                placeholder="093xxxxxx"
                value={getValues().cellPhone}
                handleChange={(e) => handleChangeInput('cellPhone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputFieldWithValidation
                formProps={formProps}
                labelKey="contactInfo.phoneNumber"
                name="phone"
                autoComplete="off"
                placeholder="010xxxxxx"
                value={getValues().phone}
                handleChange={(e) => handleChangeInput('phone', e.target.value)}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

ContactInfo.propTypes = {
  initialData: PropTypes.shape({
    country: PropTypes.shape({
      id: PropTypes.number,
    }),
    street: PropTypes.string,
    streetEn: PropTypes.string,
    state: PropTypes.shape({
      id: PropTypes.number,
    }),
    cityVillage: PropTypes.shape({
      id: PropTypes.number,
    }),
    cellPhone: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    errors: PropTypes.shape({}),
    clearError: PropTypes.func,
    triggerValidation: PropTypes.func,
    watch: PropTypes.func,
  }).isRequired,
  showEngAddress: PropTypes.bool,
  disabledInputs: PropTypes.arrayOf(PropTypes.string),
  hasPhone: PropTypes.bool,
  hasLanguageSensitiveNames: PropTypes.bool,
  hasAddressInputsLanguageSensitiveNames: PropTypes.bool,
};

ContactInfo.defaultProps = {
  showEngAddress: false,
  disabledInputs: [],
  hasPhone: false,
  hasLanguageSensitiveNames: false,
  hasAddressInputsLanguageSensitiveNames: false,
};

export default ContactInfo;
